/* AZ & CO. — the bag: quantity, and making add and remove feel instant.

   Kept out of theme.js on purpose. theme.js owns the markup and repaints
   #cart-lines wholesale after every add, remove and undo; this file reaches
   into what it painted, and reacts to presses before the server answers.

   Measured 2026-09-15 against the live store: /cart/add.js 0.7-1.2s,
   /cart.js 0.2-0.3s, /cart/change.js 0.7-2.5s. theme.js waited for all of it
   before anything on screen moved, which is what "not fluid" was.

   ---------------------------------------------------------------------
   1. Quantity. The number moves on the press, presses are collected into
      one request after a short pause, and only the newest reply may paint.

   2. Remove. The line leaves on the press and the subtotal drops with it.
      theme.js still sends the request and repaints when it lands, exactly as
      before. If the request never lands the line is put back.

   3. Add. The drawer opens on the press with the bottle already in it,
      marked as being added, built from the card that was pressed. theme.js
      replaces it with the real line when the cart comes back. If the add is
      refused, the placeholder is taken out again.

   4. The subtotal bug. This file kept its own copy of the cart and refreshed
      it only from /cart.js. A remove answers from /cart/change.js, so the
      copy went stale, and the moment theme.js repainted the lines this file
      repainted the total from the stale copy — putting the old subtotal and
      count back. Every full-cart reply is read now, and a repaint waits for
      the read in flight before it touches a number.

   The minus sign is the literal character, not an escape: the file is
   uploaded through a GraphQL block string, which does not process escapes,
   and "\\u2212" once reached the page as six visible characters.
   --------------------------------------------------------------------- */

(function () {
  'use strict';

  var state = null;       // the cart, as last seen or as last guessed
  var retail = new Map(); // handle -> Promise<Map<variant id, compare-at>>
  var reading = null;     // the cart read currently being parsed, if any

  var queued = null;      // { key, quantity } waiting for the pause to end
  var timer = null;
  var sent = 0;           // sequence of the last request sent
  var acked = 0;          // sequence of the last request painted
  var touched = false;    // has anyone used the stepper yet this page

  var WAIT = 240;
  var MINUS = '−';
  var FULL_CART = /\/cart(\/update|\/change)?\.js(\?|$)/;
  var ADD = /\/cart\/add\.js(\?|$)/;
  var still = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ahead () {
    return queued !== null || sent !== acked;
  }

  function money (cents) {
    var n = Math.round(cents / 100).toLocaleString('en-US');
    return ((window.AZ && window.AZ.currency) || 'AED') + ' ' + n;
  }

  function announce (msg) {
    var region = document.getElementById('cart-live');
    if (region) region.textContent = msg;
  }

  function itemFor (key) {
    if (!state || !state.items) return null;
    for (var i = 0; i < state.items.length; i++) {
      if (state.items[i].key === key) return state.items[i];
    }
    return null;
  }

  function recount () {
    var total = 0;
    var units = 0;
    state.items.forEach(function (x) { total += x.final_line_price; units += x.quantity; });
    state.total_price = total;
    state.item_count = units;
  }

  /* ------------------------------------------------------------- reading */

  var native = window.fetch;
  if (typeof native === 'function' && !native.azQty) {
    var wrapper = function (input) {
      var url = typeof input === 'string' ? input : ((input && input.url) || '');
      var out = native.apply(this, arguments);

      if (FULL_CART.test(url)) {
        reading = out.then(function (r) {
          if (!r || !r.ok) return;
          return r.clone().json().then(function (c) {
            if (!c || !c.items) return;
            // a read that started before a quantity press it would undo
            if (ahead()) return;
            state = c;
            paint();
          });
        }).catch(function () {});
      } else if (ADD.test(url)) {
        out.then(function (r) {
          if (!r || !r.ok) dropPending(true);
        }).catch(function () { dropPending(true); });
      } else {
        var m = url.match(/\/products\/([^/?#]+)\.js(\?|$)/);
        if (m && !retail.has(m[1])) {
          retail.set(m[1], out.then(function (r) {
            return r && r.ok ? r.clone().json() : null;
          }).then(toRetail).catch(function () { return new Map(); }));
        }
      }

      return out;
    };
    wrapper.azQty = true;
    window.fetch = wrapper;
  }

  function toRetail (p) {
    var m = new Map();
    if (p && p.variants) p.variants.forEach(function (v) { m.set(v.id, v.compare_at_price); });
    return m;
  }

  function retailMap (handle) {
    if (!retail.has(handle)) {
      retail.set(handle, native('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(toRetail)
        .catch(function () { return new Map(); }));
    }
    return retail.get(handle);
  }

  function paintSaved () {
    var row = document.getElementById('cart-saved');
    var out = document.getElementById('cart-saved-value');
    if (!row || !out || !state) return;

    var handles = [];
    state.items.forEach(function (i) {
      if (handles.indexOf(i.handle) === -1) handles.push(i.handle);
    });

    Promise.all(handles.map(retailMap)).then(function (maps) {
      var saved = 0;
      state.items.forEach(function (it) {
        var m = maps[handles.indexOf(it.handle)] || new Map();
        var r = m.get(it.variant_id);
        if (r && r > it.final_price) saved += (r - it.final_price) * it.quantity;
      });
      if (saved > 0) { out.textContent = money(saved); row.hidden = false; } else { row.hidden = true; }
    }).catch(function () { row.hidden = true; });
  }

  function stepper (qty) {
    var d = document.createElement('div');
    d.className = 'qty';
    d.setAttribute('data-qty', '');
    d.innerHTML =
      '<button type="button" class="qty-btn" data-qty-step="-1" aria-label="One fewer"' +
        (qty <= 1 ? ' disabled' : '') + '>' + MINUS + '</button>' +
      '<span class="qty-n" data-qty-n>' + qty + '</span>' +
      '<button type="button" class="qty-btn" data-qty-step="1" aria-label="One more">+</button>';
    return d;
  }

  function paint () {
    var host = document.getElementById('cart-lines');
    if (!host || !state) return;

    var lines = host.querySelectorAll('.bag-line[data-key]');
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (line.classList.contains('is-leaving')) continue;
      var it = itemFor(line.getAttribute('data-key'));
      if (!it) continue;

      var id = line.querySelector('.bag-id');
      var box = line.querySelector('[data-qty]');
      if (!box && id) {
        box = stepper(it.quantity);
        var rm = id.querySelector('[data-remove]');
        if (rm) id.insertBefore(box, rm); else id.appendChild(box);
      }
      if (box) {
        var n = box.querySelector('[data-qty-n]');
        if (n && n.textContent !== String(it.quantity)) n.textContent = it.quantity;
        var minus = box.querySelector('[data-qty-step="-1"]');
        if (minus) minus.disabled = it.quantity <= 1;
      }

      var price = line.querySelector('.bag-price');
      if (price) price.textContent = money(it.final_line_price);
    }

    var total = document.getElementById('cart-total');
    if (total) total.textContent = money(state.total_price);

    var count = document.getElementById('bag-count');
    if (count) {
      count.textContent = state.item_count;
      count.hidden = state.item_count === 0;
    }

    if (touched) paintSaved();
  }

  function guess (key, quantity) {
    var it = itemFor(key);
    if (!it) return;
    it.quantity = quantity;
    it.final_line_price = it.final_price * quantity;
    it.line_price = (it.price || it.final_price) * quantity;
    recount();
  }

  function send () {
    timer = null;
    if (!queued) return;

    var job = queued;
    queued = null;

    var mine = ++sent;
    var route = (window.AZ && window.AZ.routes && window.AZ.routes.cart_change) || '/cart/change';

    // native, not the wrapper: this reply is reconciled here, by sequence
    native(route + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: job.key, quantity: job.quantity })
    }).then(function (r) { return r.json(); }).then(function (c) {
      var line = document.querySelector('.bag-line[data-key="' + job.key + '"]');
      var got = null;
      (c.items || []).forEach(function (i) { if (i.key === job.key) got = i; });

      if (line) {
        if (got && got.quantity < job.quantity) line.setAttribute('data-capped', '');
        else line.removeAttribute('data-capped');
      }

      if (mine !== sent) return;

      acked = mine;
      if (c && c.items) { state = c; paint(); }
    }).catch(function () {
      if (mine === sent) acked = mine;
    });
  }

  /* ------------------------------------------------------------ quantity */

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var btn = target.closest('[data-qty-step]');
    if (!btn || btn.disabled) return;

    var line = btn.closest('.bag-line');
    if (!line) return;

    var key = line.getAttribute('data-key');
    var it = itemFor(key);
    if (!it) return;

    var want = it.quantity + Number(btn.getAttribute('data-qty-step'));
    if (want < 1) return;

    touched = true;
    guess(key, want);
    paint();
    announce(want + ' in your bag.');

    if (queued && queued.key !== key) { clearTimeout(timer); send(); }
    queued = { key: key, quantity: want };
    clearTimeout(timer);
    timer = setTimeout(send, WAIT);
  });

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.drawer-checkout, [name="checkout"]') && queued) {
      clearTimeout(timer);
      send();
    }
  }, true);

  document.addEventListener('visibilitychange', function () {
    if (document.hidden && queued) { clearTimeout(timer); send(); }
  });

  /* -------------------------------------------------------------- remove */

  function leave (line) {
    line.classList.add('is-leaving');
    if (still) { line.style.display = 'none'; return; }
    line.style.height = line.offsetHeight + 'px';
    line.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        line.style.height = '0px';
        line.style.paddingTop = '0px';
        line.style.paddingBottom = '0px';
        line.style.borderBottomWidth = '0px';
      });
    });
  }

  function restore (line) {
    line.classList.remove('is-leaving');
    line.style.cssText = '';
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var rm = target.closest('[data-remove]');
    if (!rm) return;
    var line = rm.closest('.bag-line');
    if (!line) return;

    if (line.classList.contains('is-leaving')) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    var key = line.getAttribute('data-key');
    if (queued && queued.key === key) { clearTimeout(timer); queued = null; }

    leave(line);

    if (state && state.items) {
      state.items = state.items.filter(function (i) { return i.key !== key; });
      recount();
      paint();
      if (!state.items.length) {
        var foot = document.getElementById('cart-foot');
        var empty = document.getElementById('cart-empty');
        setTimeout(function () {
          if (!line.isConnected) return;
          if (foot) foot.hidden = true;
          if (empty) empty.hidden = false;
        }, 300);
      }
    }

    setTimeout(function () {
      if (!line.isConnected || !line.classList.contains('is-leaving')) return;
      restore(line);
      var foot = document.getElementById('cart-foot');
      var empty = document.getElementById('cart-empty');
      if (foot) foot.hidden = false;
      if (empty) empty.hidden = true;
      native('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { if (c && c.items) { state = c; paint(); } })
        .catch(function () {});
    }, 12000);
  }, true);

  /* ----------------------------------------------------------------- add */

  var pending = null;

  function text (root, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var el = root.querySelector(selectors[i]);
      if (el && el.textContent.trim()) return el.textContent.trim();
    }
    return '';
  }

  function describe (form) {
    var card = form.closest('.spec, .rail-cell, .cab-cell, .pdp-top');
    if (!card) return null;
    var name = text(card, ['.pdp-name', '.spec-name', '.rail-name', '.cab-name']);
    if (!name) return null;
    var img = card.querySelector('#pdp-img, .spec-plate img, .rail-plate img, .cab-stand img');
    return {
      house: text(card, ['.pdp-house a', '.spec-house', '.rail-house', '.cab-house', '.pdp-house']),
      name: name,
      variant: text(card, ['#pdp-kind']),
      price: text(card, ['#pdp-now', '.spec-price .now', '.rail-price b', '.cab-money b']),
      img: img ? (img.currentSrc || img.src) : ''
    };
  }

  function drawerOpen () {
    var d = document.getElementById('cart-drawer');
    if (!d || !d.hidden) return;
    d.hidden = false;
    [].slice.call(document.body.children).forEach(function (n) {
      if (n !== d) n.setAttribute('inert', '');
    });
    document.documentElement.style.overflow = 'hidden';
    var close = d.querySelector('.drawer-close');
    if (close) close.focus();
  }

  function drawerClose () {
    var d = document.getElementById('cart-drawer');
    if (!d) return;
    d.hidden = true;
    [].slice.call(document.body.children).forEach(function (n) { n.removeAttribute('inert'); });
    document.documentElement.style.overflow = '';
  }

  function addPending (info) {
    var host = document.getElementById('cart-lines');
    if (!host) return;

    var el = document.createElement('div');
    el.className = 'bag-line bag-line--pending';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<span class="bag-plate"></span>' +
      '<div class="bag-id"><p class="bag-house"></p><span class="bag-name"></span>' +
      '<p class="bag-variant"></p><p class="bag-adding">Adding to your bag</p></div>' +
      '<p class="bag-price"></p>';

    if (info.img) {
      var im = document.createElement('img');
      im.src = info.img;
      im.width = 70;
      im.height = 94;
      im.alt = '';
      el.querySelector('.bag-plate').appendChild(im);
    }
    el.querySelector('.bag-house').textContent = info.house;
    el.querySelector('.bag-name').textContent = info.name;
    var v = el.querySelector('.bag-variant');
    if (info.variant) v.textContent = info.variant; else v.remove();
    el.querySelector('.bag-price').textContent = info.price;

    host.insertBefore(el, host.firstChild);

    var empty = document.getElementById('cart-empty');
    pending = { el: el, hadLines: host.querySelectorAll('.bag-line[data-key]').length > 0 };
    if (empty) empty.hidden = true;
  }

  function dropPending (failed) {
    if (!pending) return;
    var p = pending;
    pending = null;
    if (p.el.isConnected) p.el.remove();
    if (failed && !p.hadLines) {
      var empty = document.getElementById('cart-empty');
      if (empty) empty.hidden = false;
      drawerClose();
    }
  }

  document.addEventListener('submit', function (e) {
    var form = e.target && e.target.closest && e.target.closest('[data-cart-add]');
    if (!form) return;
    var info = describe(form);
    if (!info) return;
    dropPending(false);
    drawerOpen();
    addPending(info);
  }, true);

  function watch () {
    var host = document.getElementById('cart-lines');
    if (!host || !window.MutationObserver) return;
    new MutationObserver(function () {
      if (pending && !pending.el.isConnected) pending = null;
      Promise.resolve(reading).then(paint);
    }).observe(host, { childList: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
}());